import { Controller, useFormContext } from "react-hook-form";
import { MUTATION_UPDATE_TERMINAL } from "../../common";
import { useMutation } from "@apollo/client";
import { Terminal } from "@towersystems/roam-common/lib/generated-types";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Page,
  Stack,
  useFlashMessages,
  TextField,
  FlatActionList,
  Divider,
  TextButton,
} from "@towersystems/roam-common-native";
import { Colors, View } from "react-native-ui-lib";
import { TerminalFormValues } from "../../components/TerminalForm";
import { TerminalFormParamList } from "../../../../types";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Switch } from "react-native";

export type TerminalFormScreenProps = NativeStackScreenProps<
  TerminalFormParamList,
  "TerminalFormScreen"
>;

export const TerminalFormScreen = ({ navigation }: TerminalFormScreenProps) => {
  const { control, setValue, formState, handleSubmit, watch } =
    useFormContext<TerminalFormValues>();

  const id = watch("id");
  const flashMessagesContext = useFlashMessages();
  const title = id ? "Edit Terminal" : "Create Terminal";

  const { t } = useTranslation("settings");

  const [updateTerminalFn] = useMutation<{ updateTerminal: Terminal }>(
    MUTATION_UPDATE_TERMINAL
  );

  const paymentMethodIds = watch("paymentMethodIds");

  const handleOnSubmit = (data: TerminalFormValues) => {
    if (data.id) {
      updateTerminalFn({
        variables: {
          input: {
            id: data.id,
            name: data.name,
            saleSufix: data.saleSufix,
            salePrefix: data.salePrefix,
            enabled: data.enabled,
            paymentMethodIds: data.paymentMethodIds,
          },
        },
      })
        .then((result) => {
          if (!result.data?.updateTerminal) {
            throw new Error();
          }
          flashMessagesContext.showMessage({
            message: t("messages.terminal_saved.message"),
            severity: "success",
          });
          navigation.goBack();
        })
        .catch((error) => {
          flashMessagesContext.showMessage({
            message: t("message.generic_save_error.message"),
            severity: "error",
          });
        });
    } else {
      return; // not implemented yet
    }
  };

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_terminal")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title={title}
      secondaryTitle="Manage terminal settings"
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="General">
          <Stack vertical>
            <Controller
              control={control}
              name="name"
              render={({ field: { onChange, value } }) => (
                <TextField label="Name" value={value} onChangeText={onChange} />
              )}
            />
            <Divider marginVertical={0} />
            <FlatActionList
              items={[
                {
                  key: "terminal_enabled",
                  title: t("form.terminal_enabled.label"),
                  subTitle: t("form.terminal_enabled.description"),
                  onPress: () => {},
                  componentRight: () => (
                    <Controller
                      name="enabled"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <View>
                          <Switch
                            value={value}
                            onValueChange={() => {
                              onChange(!value);
                            }}
                          />
                        </View>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
        <Card title="Invoice Configuration">
          <Stack vertical>
            <Controller
              control={control}
              name="invoiceSequence"
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Invoice Sequence"
                  value={value.toString()}
                  onChangeText={onChange}
                  keyboardType="numeric"
                />
              )}
            />
            <Controller
              control={control}
              name="salePrefix"
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Sale Prefix"
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />
            <Controller
              control={control}
              name="saleSufix"
              render={({ field: { onChange, value } }) => (
                <TextField
                  label="Sale Sufix"
                  value={value}
                  onChangeText={onChange}
                />
              )}
            />
          </Stack>
        </Card>
        <Card title="Other">
          <FlatActionList
            items={[
              {
                title: "Select Payment Methods",
                subTitle:
                  "Select which payment methods are available for this terminal during checkout.",
                onPress: () =>
                  navigation.navigate("SelectPaymentMethodIdsScreen"),
                key: "select-template-style",
                componentRight: () => {
                  return (
                    <TextButton
                      onPress={() =>
                        navigation.navigate("SelectPaymentMethodIdsScreen")
                      }
                    >
                      {`${paymentMethodIds.length + 1} selected`}
                    </TextButton>
                  );
                },
              },
            ]}
          />
        </Card>
      </Stack>
    </Page>
  );
};
